/**
 * Next-Best-Action store (Specs/bid-next-best-action-spec.md)
 *
 * Fetched on overview mount, optimistic dismiss, no polling — workflow stage
 * changes at human speed, so it recomputes on the next visit.
 */

import { create } from 'zustand'
import { getNextActions, dismissNextAction, type NextActionInsight } from '@/lib/api'
import { captureEvent } from '@/lib/posthog'

interface NextActionsState {
  /** Bid the current insights belong to (stores are global). */
  bidId: string | null
  insights: NextActionInsight[]
  isLoading: boolean
  /** Insight ids already reported as shown this session (telemetry dedupe). */
  shownIds: Set<string>

  load: (bidId: string) => Promise<void>
  dismiss: (bidId: string, insight: NextActionInsight) => Promise<void>
  /** Fires next_action_shown once per insight per session. */
  reportShown: (bidId: string, insights: NextActionInsight[]) => void
  reportClicked: (bidId: string, insight: NextActionInsight) => void
}

function eventProps(bidId: string, insight: NextActionInsight) {
  return {
    bidId,
    ruleId: insight.ruleId,
    severity: insight.severity,
    audience: insight.audience,
  }
}

export const useNextActionsStore = create<NextActionsState>((set, get) => ({
  bidId: null,
  insights: [],
  isLoading: false,
  shownIds: new Set<string>(),

  load: async (bidId) => {
    set({ bidId, isLoading: true })
    try {
      const { insights } = await getNextActions(bidId)
      if (get().bidId === bidId) set({ insights, isLoading: false })
    } catch {
      // The block is a nudge surface — failures render nothing, never an error state
      if (get().bidId === bidId) set({ insights: [], isLoading: false })
    }
  },

  dismiss: async (bidId, insight) => {
    set((state) => ({ insights: state.insights.filter((i) => i.id !== insight.id) }))
    captureEvent('next_action_dismissed', eventProps(bidId, insight))
    try {
      await dismissNextAction(bidId, insight.id, insight.ruleId)
    } catch {
      // Optimistic removal stands — worst case the insight reappears next visit
    }
  },

  reportShown: (bidId, insights) => {
    const shownIds = new Set(get().shownIds)
    let changed = false
    for (const insight of insights) {
      if (shownIds.has(insight.id)) continue
      shownIds.add(insight.id)
      changed = true
      captureEvent('next_action_shown', eventProps(bidId, insight))
    }
    if (changed) set({ shownIds })
  },

  reportClicked: (bidId, insight) => {
    captureEvent('next_action_clicked', eventProps(bidId, insight))
  },
}))
